import { useAuth0 } from '@auth0/auth0-react';
import React, { PropsWithChildren } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { usePermissions } from '../../../hooks/usePermissions';
import { permissionRequiredFor } from '../../../utils/permissions';

/** Where a refused user is sent, with what they asked for; why is viewer-api's log, not the page's. */
export function forbiddenPath(from: string): string {
  return `/forbidden?${new URLSearchParams({ from }).toString()}`;
}

/**
 * Renders its children for a signed-in user with a role, and the page's permission where it needs one; sends
 * anyone else to /forbidden. Signing in stays the children's (the layout's `withAuthenticationRequired`, OHIF's
 * own route), so an anonymous visitor passes through to it. viewer-api refuses the same users call by call.
 */
export function RequireRole({ children }: PropsWithChildren) {
  const { isAuthenticated, isLoading: authLoading } = useAuth0();
  const { isLoading, roles, has } = usePermissions();
  const location = useLocation();

  if (!authLoading && !isAuthenticated) {
    return <>{children}</>;
  }
  if (authLoading || isLoading) {
    return <div className="p-4">Checking your access…</div>;
  }
  const from = `${location.pathname}${location.search}`;
  if (roles.length === 0) {
    return <Navigate to={forbiddenPath(from)} replace />;
  }
  const need = permissionRequiredFor(location.pathname);
  if (need && !has(need)) {
    return <Navigate to={forbiddenPath(from)} replace />;
  }
  return <>{children}</>;
}
